/**
 * Тексты интерфейса по-русски. Это исходный словарь: игра написана на нём.
 *
 * Ключи сгруппированы по экранам, а не по смыслу: правя экран, удобнее видеть
 * рядом всё, что на нём написано. Английский словарь повторяет эту структуру,
 * и повторяет обязательно — типы устроены так, что пропущенный ключ не
 * собирается.
 *
 * Значение — либо строка, либо функция, если в текст подставляется число или
 * имя. Числительные разные в разных языках, и склеивать строку из кусков
 * нельзя: «5 раундов» и «5 rounds» устроены по-разному, а «1 раунд» и вовсе
 * ломает любую склейку.
 */

import { plural } from "~/domain/format";
import { QUESTIONS } from "~/i18n/faq.ru";

export const ru = {
  language: {
    label: "Язык",
  },

  errors: {
    unavailable: "Сервер недоступен. Попробуй ещё раз",
    tooOften: "Слишком часто. Подожди немного и попробуй снова",
    status: (code: number) => `Ошибка ${String(code)}`,
  },

  app: {
    restoring: "Восстанавливаем сессию…",
    toGame: "Перейти к игре",
    setupHint: "Настрой партию и жми «Начать»",
    quitConfirm: "Завершить партию досрочно?",
  },

  topbar: {
    round: (index: number, total: number) => `Раунд ${String(index)} из ${String(total)}`,
    progress: "Прогресс партии",
    logout: "Выйти",
    quit: "Завершить",
  },

  footer: {
    players: (count: number) =>
      `Играют ${String(count)} ${plural(count, "человек", "человека", "человек")}`,
    write: "Написать нам",
    source: "Исходный код",
    navLabel: "Об игре и документы",
    /** Подпись к карте разорвана ссылкой: «© участники OpenStreetMap» */
    creditsBefore: "Карта и границы стран — © участники",
    creditsAfter: "",
  },

  notice: {
    label: "О хранилище браузера",
    text: "Куки мы не ставим — в браузере остаётся только токен входа.",
    details: "Подробнее",
    ok: "Понятно",
  },

  legal: {
    tabs: { terms: "Условия", privacy: "Конфиденциальность", storage: "Куки" },
    list: "Документы",
    close: "Закрыть",
    revision: (date: string) => `Редакция от ${date}`,
    /** Пусто: документы и так на русском */
    russianOnly: "",
  },

  auth: {
    login: "Вход",
    register: "Регистрация",
    email: "Email",
    password: "Пароль",
    showPassword: "Показать пароль",
    hidePassword: "Скрыть пароль",
    passwordPlaceholder: (min: number) => `Не короче ${String(min)} символов`,
    displayName: "Имя в таблице лидеров",
    displayNameHint: "необязательно",
    displayNamePlaceholder: "Как тебя показывать",
    acceptBefore: "Принимаю",
    acceptTerms: "условия использования",
    acceptAnd: "и",
    acceptPrivacy: "политику конфиденциальности",
    submitLogin: "Войти",
    submitRegister: "Создать аккаунт",
    noAccount: "Ещё нет аккаунта?",
    goRegister: "Зарегистрируйся",
    quick: " — это полминуты",
    onlyEmail: "Нужны только email и пароль. Писем мы не отправляем и адрес никому не передаём",
    fillBoth: "Заполни email и пароль",
    tooShort: (min: number) => `Пароль должен быть не короче ${String(min)} символов`,
    mustAccept: "Чтобы завести учётную запись, нужно принять условия",
  },

  game: {
    toTarget: "К цели",
    toNorth: "На север",
    keysMap: "карта",
    keysAnswer: "ответить",
    keysZoom: "вернуть масштаб",
    keysNorth: "на север",
    frame: (size: string) => `участок ~${size}`,
    tilesFailed: "Часть снимка не загрузилась. Подвигай карту — тайлы подгрузятся снова.",
    hint: "Подсказка",
    hintCost: (points: string) => `−${points} очков`,
    secondsLeft: (seconds: number) => `Осталось секунд: ${String(seconds)}`,

    dropPin: "Отметь место на карте мира",
    pinDropped: "Точка поставлена",
    loadingCountries: "Загружаем страны…",
    pickCountry: "Выбери страну, из которой снимок",
    collapse: "Свернуть",
    pin: "Закрепить",
    answer: "Ответить",
    openCountries: "Выбрать страну",
    openMap: "Открыть карту",
    changePin: "Изменить точку",

    choicesLabel: "Из какой страны снимок",
    whichCountry: "Из какой страны этот снимок?",
    mayChange: "Можно передумать до ответа",

    coachStep: (order: number, total: number) => `Шаг ${String(order)} из ${String(total)}`,
    coachGot: "Понятно",
    coachSkip: "Не показывать",
    coach: {
      lookTitle: "Осмотрись",
      lookText:
        "Это участок спутниковой съёмки без подписей и указателей. Крестик в центре — то самое место, которое нужно найти. Приближай колесом или щипком, тащи, чтобы осмотреть окрестности.",
      chooseTitle: "Выбери страну",
      chooseText:
        "Под снимком шесть стран, и одна из них та, откуда снимок. Ищи в кадре подсказки: растительность, крыши, разметку, язык вывесок на приближении.",
      chooseAnswer:
        "Передумать можно сколько угодно, пока не нажал «Ответить». Угадал — все пять тысяч очков за раунд, ошибся — тем больше, чем ближе названная страна к настоящей.",
      pinTitle: "Отметь место",
      countryHover:
        "Подведи курсор к карте мира в правом нижнем углу и нажми на страну, из которой, по-твоему, этот снимок. Страна под курсором подсвечивается.",
      countryTap:
        "Нажми «Выбрать страну» в правом нижнем углу и ткни на карте мира в страну, из которой, по-твоему, этот снимок.",
      pinHover:
        "Подведи курсор к карте мира в правом нижнем углу и нажми там, где, по-твоему, снят этот участок.",
      pinTap:
        "Нажми «Открыть карту» в правом нижнем углу и отметь на карте мира место, где, по-твоему, снят этот участок.",
      answerTitle: "Отвечай",
      answerCountry:
        "Ткни на карте страну, из которой этот снимок. Менять её можно сколько угодно, пока не нажал «Ответить»: угадал — все пять тысяч очков за раунд.",
      answerPin:
        "Ткни на карте место, где, по-твоему, снят участок. Двигать точку можно, пока не нажал «Ответить»: чем ближе к цели, тем больше очков — до пяти тысяч за раунд.",
    },

    resultLabel: "Результат раунда",
    outOf: (max: string) => `из ${max} очков`,
    country: "Страна",
    yourAnswer: "Твой ответ",
    missedLand: "мимо суши",
    miss: "Промах",
    accuracy: "Точность",
    usualMiss: (distance: string) => `Здесь обычно промахиваются на ${distance} — `,
    youCloser: "ты точнее",
    youFurther: "ты дальше",
    target: "цель",
    yourPin: "твоя точка",
    seeSummary: "Посмотреть итоги",
    nextRound: "Следующий раунд",

    gameOver: "Партия окончена",
    points: "очков",
    nothingPlayed: "Ни одного раунда не сыграно",
    summary: (rounds: number, average: string) =>
      `${String(rounds)} ${plural(rounds, "раунд", "раунда", "раундов")} · в среднем ${average} за раунд`,
    record: "Это твой лучший результат",
    placeIn: (place: number) => `${String(place)} место в зачёте`,
    showWholeGame: "Показать всю партию",
    roundOfZone: (index: number, zone: string) => `Раунд ${String(index)}, ${zone}`,
    setUpGame: "Настроить партию",
    playAgain: "Играть снова",
    toMenu: "В меню",

    share: "Поделиться результатом",
    shareCopied: "Скопировано в буфер",
    shareFailed: "Не получилось скопировать",
    shareScore: (score: string) => `${score} очков`,
    shareBest: (distance: string) => `Лучший раунд: ${distance}`,
    shareChallenge: (day: string) => `Location King · челлендж ${day}`,

    loadingRound: "Выбираем место…",
    loadingDistance: "Считаем расстояние…",
    loadingTimeout: "Время вышло…",
    loadingSummary: "Подводим итоги…",
    noRound: "Сервер не выдал раунд",

    tiers: {
      perfect: "В яблочко",
      great: "Отлично",
      good: "Хорошо",
      fair: "Неплохо",
      poor: "Мимо",
      awful: "Совсем не туда",
    },

    avatarOf: (name: string) => `Аватарка игрока ${name}`,
    /** Подпись под картой. Разметкой, потому что такой её принимает OpenLayers */
    mapCredit:
      '© участники <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer noopener">OpenStreetMap</a>',
    avatar: "Аватарка игрока",
    loading: "Загружаем",
  },

  room: {
    title: "Комната",
    subtitle:
      "Те же раунды для всех, кто вошёл. Играете каждый в своём темпе и сравниваете результаты.",
    fromSolo: "Условия берутся из одиночной партии:",
    editSetup: "Изменить условия",
    create: "Создать комнату",
    codePlaceholder: "КОД",
    codeLabel: "Код комнаты",
    enter: "Войти",
    mine: "Твои комнаты",
    players: (count: number) => `${String(count)} ${plural(count, "игрок", "игрока", "игроков")}`,
    leave: "Выйти",
    meta: (rounds: number, time: string, host: string) =>
      `${String(rounds)} ${plural(rounds, "раунд", "раунда", "раундов")} · ${time} · хост ${host}`,
    closed: " · набор закрыт",
    copyLink: "Скопировать ссылку",
    linkCopied: "Ссылка скопирована",
    copyFailed: "Не получилось скопировать",
    nobodyYet: "Пока никто не вошёл. Отправь ссылку друзьям.",
    /** Значок рядом с именем: выдавать бота за человека нельзя */
    bot: "бот",
    play: "Играть",
    signupsClosed: "Набор закрыт — войти уже нельзя.",
    resume: "Продолжить партию",
    myScore: "Твой результат",
    closeSignups: "Закрыть набор",
    notFound: "Комната не найдена",
    createFailed: "Не удалось создать комнату",
    enterFailed: "Не удалось войти в комнату",
    closeFailed: "Не удалось закрыть набор",
  },

  board: {
    title: "Таблица лидеров",
    metric: "Метрика",
    scope: "Зачёт",
    scopes: {
      all: "Все партии",
      friends: "Друзья",
      easy: "Легко",
      normal: "Средне",
      hard: "Сложно",
      hardcore: "Хардкор",
      russia: "Россия",
      usa: "США",
      eu: "Евросоюз",
    },
    metrics: {
      best: { label: "Партия", hint: "Очков за раунд в лучшей партии" },
      total: { label: "Сумма", hint: "Сумма очков за все партии" },
      accuracy: { label: "Точность", hint: "Средний промах за раунд, от пяти раундов" },
      sharp: { label: "Меткость", hint: "Раундов, взятых почти в точку" },
      games: { label: "Партий", hint: "Сколько партий доиграно до конца" },
    },
    emptyAll: "Пока никто не сыграл ни одной партии. Займи первое место",
    emptyFriends: "Ни ты, ни твои друзья ещё не доиграли ни одной партии",
    emptyScope: "На этих условиях ещё никто не играл. Займи первое место",
    failed: "Не удалось загрузить таблицу",
  },

  profile: {
    title: "Профиль",
    games: "Партий",
    rounds: "Раундов",
    best: "Лучшая партия",
    averageMiss: "Средний промах",
    rating: "Рейтинг",
    theme: "Оформление",
    themes: { dark: "Тёмная", light: "Светлая", system: "Как в системе" },
    themeFailed: "Не удалось запомнить оформление",

    edit: "Изменить",
    replacePicture: "Заменить",
    removePicture: "Убрать",
    uploadPicture: "Загрузить свою картинку",
    cancel: "Отмена",
    save: "Сохранить",
    name: "Имя в таблице",
    nameHint: (max: number) => `его видят другие игроки, до ${String(max)} символов`,
    avatar: "Аватарка",
    ownPicture: "Своя картинка",
    ownPictureHint: "Её видно другим игрокам в таблицах и в комнате",
    patterns: "Узор аватарки",
    pattern: (index: number) => `Узор ${String(index)}`,
    colors: "Цвет аватарки",
    color: (index: number) => `Цвет ${String(index)}`,
    tooHeavy: "Картинка тяжелее четырёх мегабайт",
    uploadFailed: "Не удалось загрузить картинку",
    removeFailed: "Не удалось убрать картинку",
    saveFailed: "Не удалось сохранить",
  },

  friends: {
    title: "Друзья",
    byCode: "Добавляют по коду игрока — имя для этого не подходит",
    waiting: (count: number) =>
      `${String(count)} ${plural(count, "заявка ждёт", "заявки ждут", "заявок ждут")} ответа`,
    invite: "Позвать",
    accept: "Принять",
    myCode: "Твой код",
    copy: "Скопировать",
    copied: "Скопирован",
    friendCode: "Код друга",
    empty: "Пока никого. Обменяйся кодами — и увидишь общий зачёт.",
    invitesYou: "зовёт тебя",
    awaitingAnswer: "ждёт ответа",
    remove: (name: string) => `Убрать ${name}`,
    listFailed: "Не удалось прочитать список друзей",
    inviteFailed: "Не удалось отправить заявку",
    acceptFailed: "Не удалось принять заявку",
    removeFailed: "Не удалось убрать",
  },

  history: {
    title: "Последние партии",
    empty: "Ты ещё не сыграл ни одной партии",
    rounds: (count: number) => `${String(count)} ${plural(count, "раунд", "раунда", "раундов")}`,
    status: { finished: "завершена", abandoned: "брошена", active: "не доиграна" },
  },

  feedback: {
    open: "Отзыв об игре",
    thanks:
      "Прочитаю всё. Ответить лично не обещаю, но чинить и делать буду именно по таким письмам.",
    invitation: "Пиши прямо: что понравилось, что раздражает, что не работает.",
    close: "Закрыть",
    cancel: "Отмена",
    send: "Отправить",
    label: "Отзыв об игре",
    title: "Как тебе игра?",
    sent: "Дошло, спасибо",
    about: "О чём",
    kinds: { impression: "Впечатление", problem: "Проблема" },
    hints: {
      impression: "Что понравилось, а что нет",
      problem: "Что случилось и на каком экране",
    },
    message: "Сообщение",
    left: (count: number) => `осталось ${String(count)}`,
    empty: "Напиши, что случилось",
    failed: "Не удалось отправить",
  },

  deleteAccount: {
    open: "Удалить аккаунт",
    warning:
      "Вместе с ним исчезнут все партии, раунды, место в таблице лидеров и созданные тобой комнаты. Восстановить это будет нечем.",
    cancel: "Отмена",
    confirm: "Удалить навсегда",
    label: "Удаление учётной записи",
    title: "Удалить аккаунт?",
    password: "Пароль",
    passwordPlaceholder: "Подтверди, что это ты",
    needPassword: "Введи пароль",
    failed: "Не удалось удалить учётную запись",
  },

  duel: {
    title: "Дуэль",
    subtitle: "Соперник подбирается по рейтингу",
    yourRating: "твой рейтинг",
    noDuelsYet: " · дуэлей ещё не было",
    rulesUnknown: "Одни и те же раунды у обоих",
    rules: (rounds: number, time: string) =>
      `${String(rounds)} ${plural(rounds, "раунд", "раунда", "раундов")} · ${time} на раунд · одни и те же места у обоих`,
    find: "Найти соперника",
    joining: "Соперник найден…",
    cancel: "Отменить поиск",
    lookingFor: "Ищем соперника",
    found: "Соперник найден",

    nobody: "Сейчас никто не ищет",
    onlyYou: "Пока ищешь только ты",
    searching: (others: number) =>
      `${String(others)} ${plural(others, "игрок ищет", "игрока ищут", "игроков ищут")} соперника`,

    /** Никто не ищет, но есть с кем сыграть: очередь — не единственный путь */
    nobodyButBot: "Никто не ищет — но есть бот",
    playBot: "Сыграть с ботом",
    botStarting: "Собираем дуэль…",
    /** Под кнопкой: бот назван ботом, и цена дуэли с ним названа тоже */
    botNote: "Бот отвечает как игрок своего уровня. Рейтинг за дуэль с ним не меняется",
    playingNow: (count: number) =>
      `Сейчас ${plural(count, "играет", "играют", "играют")} ${String(count)} ${plural(count, "человек", "человека", "человек")}`,
  },

  daily: {
    title: "Челлендж дня",
    subtitle: (rounds: number) =>
      `${String(rounds)} ${plural(rounds, "раунд", "раунда", "раундов")} — одни и те же для всех. Одна попытка в сутки.`,
    streak: (days: number) => `${plural(days, "день", "дня", "дней")} подряд`,
    keepStreak: " — сыграй сегодня, чтобы не прервать",
    bestStreak: (days: number) => ` · рекорд ${String(days)}`,
    myResult: "Твой результат сегодня",
    onlyYouPlayed: "Ты пока единственный, кто сыграл",
    nobodyFinished: "Сегодня ещё никто не доиграл до конца",
    play: "Играть челлендж",
    resume: "Продолжить челлендж",
    lost: "Партия этого дня брошена — её закрыла другая начатая игра. Челлендж вернётся завтра.",
    failed: "Не удалось начать челлендж",

    onceADay: "Одна попытка в сутки",
    playedStatus: (score: string) => `Сыгран · ${score}`,
    unfinishedStatus: "Партия не доиграна",
    abandonedStatus: "Партия брошена",
    freshStatus: "Сегодня ещё не сыгран",
    streakStatus: (days: number) =>
      `Серия ${String(days)} ${plural(days, "день", "дня", "дней")} — не прерывай`,
  },

  setup: {
    answerMode: "Чем отвечать",
    answerModes: {
      choice: {
        label: "Из шести",
        hint: "Под снимком шесть стран, одна верная. Самый простой способ начать",
      },
      country: {
        label: "Страной",
        hint: "Карта со странами: попал — максимум, мимо — по промаху",
      },
      point: { label: "Точкой", hint: "Точка на карте мира, очки за близость к цели" },
    },

    level: "Сложность",
    levels: {
      easy: { label: "Легко", hint: "Узнают по силуэту: Париж, Венеция, Манхэттен" },
      normal: {
        label: "Средне",
        hint: "Крупные города знакомых стран: Гамбург, Казань, Сиэтл",
      },
      hard: {
        label: "Сложно",
        hint: "Города, о которых знают мало, и обжитая местность без города",
      },
      hardcore: { label: "Хардкор", hint: "Дикая природа: горы, пустыни, тайга, лёд" },
    },

    place: "Где играем",
    places: {
      world: "Весь мир",
      russia: "Россия",
      usa: "США",
      eu: "Евросоюз",
      europe: "Европа",
      asia: "Азия",
      africa: "Африка",
      northAmerica: "Сев. Америка",
      southAmerica: "Юж. Америка",
      oceania: "Океания",
    },

    rounds: "Раундов",
    time: "Время на раунд",
    timeHint: "Чем быстрее ответ, тем больше очков",

    zonesFound: (count: number) => `Подходящих зон: ${String(count)}`,
    countryNote: "В режиме стран играем по всему миру: выбранное место подсказывало бы ответ.",
    noZones: "На таких условиях зон нет. Возьми другой уровень или другое место.",
    start: "Начать игру",

    soloTitle: "Одиночная партия",
    soloSubtitle: "Раунд за раундом, только ты и снимок",
    soloNewcomer:
      "Для первой партии всё уже выставлено: пять раундов по городам, которые узнают все. Просто жми «Начать»",
    landmarksTitle: "Известные места",
    landmarksSubtitle:
      "Пирамиды Гизы, Колизей, Тадж-Махал, Пальма Джумейра — крупным планом, без города вокруг",

    /** Условия одной строкой: сколько раундов, уровень, место, время */
    describeRounds: (count: number) =>
      `${String(count)} ${plural(count, "раунд", "раунда", "раундов")}`,
    describeCountry: "ответ страной",
    describeChoice: "выбор из шести",
  },

  menu: {
    modes: "Режим игры",
    sections: "Разделы",
    section: {
      profile: "Профиль",
      friends: "Друзья",
      board: "Таблица",
      history: "История",
    },

    solo: "Одиночная",
    landmarks: "Известные места",
    landmarksStatus: "Пирамиды, Колизей, Тадж-Махал",
    daily: "Челлендж дня",
    duel: "Дуэль",
    room: "Комната",
    roomStatus: "Своей компанией по коду",
    soloStatus: (answerMode: string, rounds: number) =>
      `${answerMode} · ${String(rounds)} ${plural(rounds, "раунд", "раунда", "раундов")}`,

    unfinished: "У тебя есть незаконченная партия",
    roundOf: (index: number, total: number) => `Раунд ${String(index)} из ${String(total)}`,
    resume: "Продолжить",
    replaceGame: (index: number, total: number) =>
      `Незаконченная партия (раунд ${String(index)} из ${String(total)}) будет брошена, и очки за неё не засчитаются. Начать новую?`,
  },

  demo: {
    /** Приглашение на лендинге */
    invite: "Сыграть пять раундов без регистрации",
    inviteNote: "Ничего заводить не надо: пять известных мест, минуты три",

    loading: "Готовим места…",
    leave: "Выйти",
    leaveConfirm: "Выйти из знакомства? Результат никуда не сохраняется.",
    roundOf: (index: number, total: number) => `Место ${String(index)} из ${String(total)}`,

    /** Плашка про режим текущего раунда */
    modeChoice: "Отвечаем из списка",
    modeChoiceNote: "Под снимком шесть стран, одна верная. Карта не нужна",
    modeCountry: "Теперь по карте",
    modeCountryNote: "Списка больше нет: нажми на карте страну, из которой снимок",
    modePoint: "А это уже настоящая игра",
    modePointNote: "Не страна, а место: ставь точку, и считаются километры до цели",

    finishTitle: "Пять мест позади",
    finishScore: (score: string) => `${score} очков за знакомство`,
    /** Итог того, что человек только что попробовал */
    finishLead:
      "Ты только что прошёл три способа играть: из списка, по карте стран и точкой. " +
      "Дальше начинается сама игра.",

    perksTitle: "Что даёт учётная запись",
    perks: [
      {
        title: "297 мест вместо пяти",
        text: "Города, побережья, горы и пустыни на всех континентах — и четыре уровня сложности, от Венеции до тайги без единой дороги.",
      },
      {
        title: "Челлендж дня",
        text: "Пять мест, одинаковых для всех, одна попытка в сутки. Вечером видно, кто угадал точнее.",
      },
      {
        title: "Комната для своих",
        text: "Кидаешь друзьям код — и все играют одни и те же раунды. Спорить о том, кому попались места полегче, не выйдет.",
      },
      {
        title: "История и таблица лидеров",
        text: "Партии сохраняются, промахи считаются, а в пяти зачётах видно, что у тебя выходит лучше — держать уровень или попадать в точку.",
      },
    ],

    signUp: "Завести аккаунт",
    signUpNote: "Почта и пароль, полминуты. Писем мы не шлём",
    again: "Пройти знакомство заново",
  },

  landing: {
    eyebrow: "Игра по спутниковым снимкам",
    titleTop: "Найди место",
    titleBottom: "по одному снимку",
    lead:
      "Подписи, указатели, координаты — ничего этого нет. Ставь точку на карте мира и " +
      "смотри, на сколько промахнулся: километр стоит почти пять тысяч очков, " +
      "пятьсот — около тысячи.",
    how: "Как это устроено",
    honest:
      "Ни рекламы, ни счётчиков, ни cookie. Страница не ходит ни на один чужой сайт — " +
      "даже шрифты лежат свои.",

    howTitle: "Как проходит раунд",
    steps: [
      {
        title: "Смотришь на квадрат съёмки",
        text: "Приближать можно на четыре шага — до машин во дворе. А вот выехать за край квадрата нельзя: что попало в кадр, то и все улики.",
      },
      {
        title: "Ставишь точку на карте",
        text: "Карта мира обычная, с названиями и границами. Двигать точку можно сколько угодно, пока не нажал «Ответить».",
      },
      {
        title: "Узнаёшь, на сколько промахнулся",
        text: "Карта показывает твою точку, настоящую и линию между ними. Ноль получить трудно: для этого надо промахнуться на восемь тысяч километров.",
      },
    ],

    modesTitle: "Во что играть",
    modes: [
      {
        title: "Челлендж дня",
        text: "Пять раундов, одинаковых для всех, одна попытка в сутки. Сыграл плохо — переигрывать нечего, приходи завтра.",
      },
      {
        title: "Комнаты для компании",
        text: "Создаёшь комнату, кидаешь ссылку друзьям. Раунды у всех одни и те же, так что спорить о том, кому попались места полегче, не выйдет.",
      },
      {
        title: "Таблица лидеров",
        text: "Пять зачётов, а не один: в общем на первое место рано или поздно выходит тот, кто просто много играл. Отдельно считаются лучшая партия, средний промах и меткие раунды.",
      },
    ],

    fairEyebrow: "Насколько тяжело",
    fairTitle: "Выбираешь сам",
    fairText:
      "На лёгком достаются места, которые узнают по силуэту, — Париж, Венеция, Манхэттен. " +
      "На среднем крупные города знакомых стран: Гамбург, Казань, Сиэтл. Дальше города, о " +
      "которых знают мало, и поля без единой вывески. На хардкоре остаются горы, пустыни и " +
      "тайга. Кадр там, кстати, шире, чем на лёгком: в тайге не сориентируешься по кварталу, " +
      "нужен рельеф целиком.",
    scorePerRound: "Очков за раунд",
    placesInGame: "Мест в игре",
    zoomLevels: "Уровней приближения",

    faqTitle: "Частые вопросы",
    questions: QUESTIONS,

    finishTitle: "Пять раундов. Это минут десять.",
    finishCta: "Начать игру",
  },
};
